import React, { useEffect, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../firebaseConfig';
import { Tabs, Tab } from './Tabs';
import { FaCheck, FaStar, FaTimes } from 'react-icons/fa';
import TeamName from './TeamName';

const triplePlayResult = (winner, side) => {
  if (!winner) return null;
  if (winner === 'push' || winner === side) return 'win';
  return 'loss';
};

const GameStats = () => {
  const [gamesByWeek, setGamesByWeek] = useState({});
  const [userPicks, setUserPicks] = useState({});
  const [teamPickPercentage, setTeamPickPercentage] = useState({});
  const [combinedPickPercentage, setCombinedPickPercentage] = useState({});
  const [triplePlaySummary, setTriplePlaySummary] = useState({});

  useEffect(() => {
    const gamesRef = ref(database, 'games');
    const picksRef = ref(database, 'picks');

    const unsubscribeGames = onValue(gamesRef, (snapshot) => {
      const data = snapshot.val();
      const groupedGames = data ? Object.keys(data).reduce((acc, id) => {
        const game = { id, ...data[id] };
        const week = game.weekNumber;
        if (!acc[week]) acc[week] = [];
        acc[week].push(game);
        return acc;
      }, {}) : {};

      const sortedGroupedGames = Object.keys(groupedGames)
        .map(Number)
        .sort((a, b) => b - a)
        .reduce((acc, week) => {
          acc[week] = groupedGames[week];
          return acc;
        }, {});

      setGamesByWeek(sortedGroupedGames);
    });

    const unsubscribePicks = onValue(picksRef, (snapshot) => {
      setUserPicks(snapshot.val() || {});
    });

    return () => {
      unsubscribeGames();
      unsubscribePicks();
    };
  }, []);

  useEffect(() => {
    if (Object.keys(gamesByWeek).length > 0) {
      const calculateTeamPickPercentage = (gamesByWeek, userPicks) => {
        const teamPickPercentage = {};
        const combinedPickPercentage = {};
        const triplePlaySummary = {};

        // Sort weeks in descending order
        const sortedWeeks = Object.keys(gamesByWeek).map(Number).sort((a, b) => b - a);

        sortedWeeks.forEach(week => {
          const games = gamesByWeek[week];
          const weekPickPercentage = {};
          let totalHomePicks = 0;
          let totalAwayPicks = 0;
          let totalGames = 0;
          let totalTriplePlays = 0;

          games.forEach(game => {
            const teamPicks = { home: 0, away: 0 };
            const triplePlayPicks = { home: 0, away: 0 };

            Object.keys(userPicks).forEach(userId => {
              const userPick = userPicks[userId]?.[game.id];
              if (userPick) {
                if (userPick.pick === 'home') {
                  teamPicks.home += 1;
                  if (userPick.triplePlay) triplePlayPicks.home += 1;
                } else if (userPick.pick === 'away') {
                  teamPicks.away += 1;
                  if (userPick.triplePlay) triplePlayPicks.away += 1;
                }
              }
            });

            const totalPicks = teamPicks.home + teamPicks.away;
            const homePickPercentage = totalPicks > 0 ? Math.round((teamPicks.home / totalPicks) * 100) : '0';
            const awayPickPercentage = totalPicks > 0 ? Math.round((teamPicks.away / totalPicks) * 100) : '0';

            weekPickPercentage[game.id] = {
              homeTeam: game.homeTeam,
              awayTeam: game.awayTeam,
              homeLogo: game.homeLogo,
              awayLogo: game.awayLogo,
              homePickPercentage,
              awayPickPercentage,
              homeTriplePlayCount: triplePlayPicks.home,
              awayTriplePlayCount: triplePlayPicks.away,
              locked: game.locked,
              winner: game.winner
            };

            totalHomePicks += teamPicks.home;
            totalAwayPicks += teamPicks.away;
            totalGames += 1;
            totalTriplePlays += triplePlayPicks.home + triplePlayPicks.away;
          });

          Object.keys(weekPickPercentage).forEach(gameId => {
            const stats = weekPickPercentage[gameId];
            stats.homeTriplePlayPercentage = totalTriplePlays > 0
              ? Math.round((stats.homeTriplePlayCount / totalTriplePlays) * 100)
              : 0;
            stats.awayTriplePlayPercentage = totalTriplePlays > 0
              ? Math.round((stats.awayTriplePlayCount / totalTriplePlays) * 100)
              : 0;
          });

          teamPickPercentage[week] = weekPickPercentage;
          combinedPickPercentage[week] = {
            homePickPercentage: totalGames > 0 ? Math.round((totalHomePicks / (totalHomePicks + totalAwayPicks)) * 100) : '0',
            awayPickPercentage: totalGames > 0 ? Math.round((totalAwayPicks / (totalHomePicks + totalAwayPicks)) * 100) : '0'
          };
          triplePlaySummary[week] = Object.keys(weekPickPercentage)
            .filter(gameId => weekPickPercentage[gameId].locked)
            .flatMap(gameId => {
              const stats = weekPickPercentage[gameId];
              const rows = [];
              if (stats.awayTriplePlayCount > 0) {
                rows.push({
                  gameId,
                  side: 'away',
                  team: stats.awayTeam,
                  logo: stats.awayLogo,
                  percentage: stats.awayTriplePlayPercentage,
                  count: stats.awayTriplePlayCount,
                  result: triplePlayResult(stats.winner, 'away'),
                });
              }
              if (stats.homeTriplePlayCount > 0) {
                rows.push({
                  gameId,
                  side: 'home',
                  team: stats.homeTeam,
                  logo: stats.homeLogo,
                  percentage: stats.homeTriplePlayPercentage,
                  count: stats.homeTriplePlayCount,
                  result: triplePlayResult(stats.winner, 'home'),
                });
              }
              return rows;
            })
            .sort((a, b) => b.percentage - a.percentage || b.count - a.count || a.team.localeCompare(b.team));
        });

        setCombinedPickPercentage(combinedPickPercentage);
        setTriplePlaySummary(triplePlaySummary);
        return teamPickPercentage;
      };

      setTeamPickPercentage(calculateTeamPickPercentage(gamesByWeek, userPicks));
    }
  }, [gamesByWeek, userPicks]);

  const weeksWithLockedGames = Object.keys(teamPickPercentage)
    .filter((week) => Object.values(teamPickPercentage[week]).some((stats) => stats.locked))
    .reverse();

  if (weeksWithLockedGames.length === 0) {
    return (
      <div className="max-w-4xl mx-auto pt-4">
        <h3 className="text-xl mb-2">Game Stats</h3>
        <p className="text-gray-400">No locked games to show yet.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pt-4">
      <h3 className="text-xl mb-2">Game Stats</h3>
      <Tabs highestWeek={1}>
        {weeksWithLockedGames.map(week => (
          <Tab key={week} label={`Week ${week}`}>
            <div className="text-white">
              {Object.keys(teamPickPercentage[week]).map(gameId => {
                const stats = teamPickPercentage[week][gameId];
                const game = gamesByWeek[week]?.find((weekGame) => weekGame.id === gameId);
                if (!stats.locked || !game) {
                  return null;
                }

                return (
                <div key={gameId} className="mt-4">
                    <div className="mt-2">
                      <div className="flex justify-between mb-1">
                        <span className="flex flex-col items-start">
                          <span>{stats.awayPickPercentage}%</span>
                          <strong className="flex items-center gap-1 flex-wrap">
                            <TeamName name={stats.awayTeam} logo={stats.awayLogo} size="sm" />
                            {(stats.winner === 'away' || stats.winner === 'push') && (
                              <FaCheck className="text-green-500" />
                            )}
                          </strong>
                        </span>
                        <span className="flex flex-col items-end">
                          <span>{stats.homePickPercentage}%</span>
                          <strong className="flex items-center gap-1 flex-wrap justify-end">
                            {(stats.winner === 'home' || stats.winner === 'push') && (
                              <FaCheck className="text-green-500" />
                            )}
                            <TeamName name={stats.homeTeam} logo={stats.homeLogo} size="sm" />
                          </strong>
                        </span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-full bg-gray-700 rounded-full h-4 relative">
                          <div
                            className={`h-4 absolute left-0 ${parseFloat(stats.awayPickPercentage) > parseFloat(stats.homePickPercentage) ? 'bg-accent' : 'bg-gray-500'} rounded-l-full`}
                            style={{ width: `${stats.awayPickPercentage}%` }}
                          ></div>
                          <div
                            className={`h-4 absolute right-0 ${parseFloat(stats.homePickPercentage) > parseFloat(stats.awayPickPercentage) ? 'bg-accent' : 'bg-gray-500'} rounded-r-full`}
                            style={{ width: `${stats.homePickPercentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                </div>
                );
              })}
            </div>
            {triplePlaySummary[week]?.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-700">
                <h4 className="text-sm font-semibold text-yellow-400 flex items-center gap-1.5 mb-1">
                  <FaStar /> Triple Play
                </h4>
                <p className="text-xs text-gray-400 mb-3">Share of all triple plays this week</p>
                <ul className="space-y-2">
                  {triplePlaySummary[week].map((entry) => (
                    <li key={`${entry.gameId}-${entry.side}`} className="flex items-center gap-2">
                      <span className="min-w-0 max-w-[42%] flex items-center gap-1">
                        <TeamName name={entry.team} logo={entry.logo} size="sm" />
                        {entry.result === 'win' && <FaCheck className="text-green-500 flex-shrink-0" title="Won" />}
                        {entry.result === 'loss' && <FaTimes className="text-red-500 flex-shrink-0" title="Lost" />}
                      </span>
                      <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-2 bg-yellow-400 rounded-full"
                          style={{ width: `${entry.percentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-yellow-400 w-10 text-right flex-shrink-0">{entry.percentage}%</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <br className="my-4" />
            <div className="mt-8">
              <div className="flex justify-between mb-1">
                <div className="flex flex-col items-start">
                  <span>{isNaN(combinedPickPercentage[week].awayPickPercentage) ? "0.00" : combinedPickPercentage[week].awayPickPercentage}%</span>
                  <strong>Away</strong>
                </div>
                <div className="flex flex-col items-end">
                  <span>{isNaN(combinedPickPercentage[week].homePickPercentage) ? "0.00" : combinedPickPercentage[week].homePickPercentage}%</span>
                  <strong>Home</strong>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-full bg-gray-700 rounded-full h-4 relative">
                  <div
                    className={`h-4 absolute left-0 ${parseFloat(combinedPickPercentage[week].awayPickPercentage) > parseFloat(combinedPickPercentage[week].homePickPercentage) ? 'bg-accent' : 'bg-gray-500'} rounded-l-full`}
                    style={{ width: `${combinedPickPercentage[week].awayPickPercentage}%` }}
                  ></div>
                  <div
                    className={`h-4 absolute right-0 ${parseFloat(combinedPickPercentage[week].homePickPercentage) > parseFloat(combinedPickPercentage[week].awayPickPercentage) ? 'bg-accent' : 'bg-gray-500'} rounded-r-full`}
                    style={{ width: `${combinedPickPercentage[week].homePickPercentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </Tab>
        ))}
      </Tabs>
    </div>
  );
};

export default GameStats;
